import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMY } from "@/lib/date";
import { CalendarBlank } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

const MONTH_LABEL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseFYE(s) {
  if (!s) return null;
  const m = String(s).replace("/", "-").split("-");
  if (m.length !== 2) return null;
  const mm = parseInt(m[0], 10), dd = parseInt(m[1], 10);
  if (!mm || mm < 1 || mm > 12) return null;
  return { m: mm, d: dd || 31 };
}

export default function CompaniesByFYE() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/clients").then(r => setRows(r.data)); }, []);

  const grouped = useMemo(() => {
    const buckets = Array.from({ length: 13 }, () => []); // 1..12 + 0 = unknown
    for (const c of rows) {
      if (q && !`${c.name} ${c.ssm_no}`.toLowerCase().includes(q.toLowerCase())) continue;
      const p = parseFYE(c.financial_year_end);
      buckets[p ? p.m : 0].push({ ...c, _fyeParts: p });
    }
    // sort by day of month within each bucket
    for (let i = 0; i < buckets.length; i++) {
      buckets[i].sort((a, b) => (a._fyeParts?.d || 0) - (b._fyeParts?.d || 0) || a.name.localeCompare(b.name));
    }
    return buckets;
  }, [rows, q]);

  const totalMatched = grouped.reduce((n, b) => n + b.length, 0);

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Companies</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">Company By Financial Year End</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every client grouped by FYE month — plan audit workload across the year.
        </p>
      </header>

      <div className="mb-4 max-w-md">
        <Input placeholder="Search by name or SSM no." value={q} onChange={e => setQ(e.target.value)}
               className="rounded-sm h-10" data-testid="fye-search-input" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[...Array(12).keys()].map(i => {
          const month = i + 1;
          const items = grouped[month];
          return (
            <section key={month} className="border border-border" data-testid={`fye-month-${month}`}>
              <header className="flex items-center justify-between px-4 py-2 border-b border-border">
                <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
                  <CalendarBlank size={14} weight="duotone" /> {MONTH_LABEL[i]}
                </div>
                <span className="font-mono text-[10px] text-muted-foreground">{items.length}</span>
              </header>
              {items.length === 0 ? (
                <div className="px-4 py-6 text-xs text-muted-foreground">—</div>
              ) : items.map(c => (
                <Link key={c.id} to={`/clients/${c.id}`}
                      className="block px-4 py-2 border-b last:border-b-0 border-border hover:bg-muted/50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{c.name}</div>
                      <div className="text-[11px] font-mono text-muted-foreground truncate">{c.ssm_no}</div>
                    </div>
                    <div className="font-mono text-xs shrink-0">
                      {String(c._fyeParts.d).padStart(2, "0")}/{String(c._fyeParts.m).padStart(2, "0")}
                    </div>
                  </div>
                </Link>
              ))}
            </section>
          );
        })}
        {grouped[0].length > 0 && (
          <section className="border border-dashed border-border md:col-span-2 xl:col-span-3">
            <header className="px-4 py-2 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground">
              FYE not set · {grouped[0].length}
            </header>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {grouped[0].map(c => (
                <Link key={c.id} to={`/clients/${c.id}`}
                      className="px-4 py-2 border-r border-b border-border hover:bg-muted/50">
                  <div className="text-sm truncate">{c.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">{c.ssm_no}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="mt-6 text-xs font-mono text-muted-foreground">Showing {totalMatched} companies</div>
    </div>
  );
}
